'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import { Button, Dropdown, LoadingIndicator } from '@commenda/kakashi';
import type { FieldDefinition } from '@commenda/types/src/commenda-os-dsl';
import { ChevronDown, X } from 'lucide-react';
import { useMemo } from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * Field type information passed to onSelect callback
 */
export interface FieldTypeInfo {
  type: string;
  parsingType?: string;
  /** Path used for update operations (may differ from read path) */
  updatePath?: string;
  /** Path used for create operations (may differ from read path) */
  createPath?: string;
}

interface SchemaFieldPickerProps {
  /** The entity to fetch schema for (e.g., 'corporation', 'company') */
  entity: string;
  /** Currently selected field path */
  value?: string;
  /** Callback when a field is selected */
  onSelect: (
    path: string,
    label: string,
    fieldTypeInfo?: FieldTypeInfo
  ) => void;
  /** Placeholder text when no field is selected */
  placeholder?: string;
  /** Filter to only show readable fields */
  readableOnly?: boolean;
  /**
   * Filter to only show editable fields (fields with update or create operations).
   * This is determined by the DSL schema - if a field has no update/create path,
   * it's considered read-only and will be excluded when this is true.
   */
  editableOnly?: boolean;
}

interface DropdownMenuItem {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  subMenu?: {
    groups: Array<{
      label?: string;
      items: DropdownMenuItem[];
    }>;
  };
}

interface FieldInfo {
  path: string;
  label: string;
  breadcrumb: string[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Recursively builds dropdown menu items from schema fields
 */
function buildFieldMenuItems(
  fields: Record<string, FieldDefinition>,
  parentPath: string,
  parentBreadcrumb: string[],
  onSelect: (
    path: string,
    label: string,
    breadcrumb: string[],
    fieldTypeInfo: FieldTypeInfo
  ) => void,
  readableOnly: boolean,
  editableOnly: boolean
): DropdownMenuItem[] {
  const items: DropdownMenuItem[] = [];

  for (const [key, field] of Object.entries(fields)) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const currentBreadcrumb = [...parentBreadcrumb, field.label];

    if (field.type === 'object' && field.fields) {
      // Object field with nested fields - create submenu
      const nestedItems = buildFieldMenuItems(
        field.fields,
        currentPath,
        currentBreadcrumb,
        onSelect,
        readableOnly,
        editableOnly
      );

      // Only add if there are nested items
      if (nestedItems.length > 0) {
        items.push({
          label: field.label,
          subMenu: {
            groups: [{ items: nestedItems }],
          },
        });
      }
    } else {
      // Leaf field - check if it's readable
      const isReadable = !!field.read;
      const isComputed = field.read?.computed;
      const isEditable = !!field.update || !!field.create;

      if (readableOnly && !isReadable) {
        continue;
      }

      // Skip non-editable fields if editableOnly is true
      // A field is editable if it has an update or create operation defined
      if (editableOnly && !isEditable) {
        continue;
      }

      // Get the actual read path from the schema
      const readPath = field.read?.path ?? currentPath;

      // Format label with type indicator
      let typeLabel: string = field.type;
      if (field.type === 'enum' && field.enum) {
        typeLabel = field.enum;
      }

      const displayLabel = isComputed
        ? `${field.label} (computed)`
        : `${field.label}`;

      const fieldTypeInfo: FieldTypeInfo = {
        type: field.type,
        parsingType: field.parsingType,
        updatePath: field.update?.path,
        createPath: field.create?.path,
      };

      items.push({
        label: displayLabel,
        disabled: isComputed, // Computed fields are shown but disabled
        onClick: () =>
          onSelect(readPath, field.label, currentBreadcrumb, fieldTypeInfo),
      });
    }
  }

  return items;
}

/**
 * Find field info by path in the schema
 */
function findFieldByPath(
  fields: Record<string, FieldDefinition>,
  targetPath: string,
  parentPath: string = '',
  breadcrumb: string[] = []
): FieldInfo | null {
  for (const [key, field] of Object.entries(fields)) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const currentBreadcrumb = [...breadcrumb, field.label];

    // Check if this field's read path matches
    if (field.read?.path === targetPath) {
      return {
        path: targetPath,
        label: field.label,
        breadcrumb: currentBreadcrumb,
      };
    }

    // Check if the current path matches (for fields without explicit read path)
    if (currentPath === targetPath) {
      return {
        path: targetPath,
        label: field.label,
        breadcrumb: currentBreadcrumb,
      };
    }

    // Recurse into nested fields
    if (field.type === 'object' && field.fields) {
      const found = findFieldByPath(
        field.fields,
        targetPath,
        currentPath,
        currentBreadcrumb
      );
      if (found) return found;
    }
  }

  return null;
}

// =============================================================================
// Component
// =============================================================================

export function SchemaFieldPicker({
  entity,
  value,
  onSelect,
  placeholder = 'Select a field...',
  readableOnly = true,
  editableOnly = false,
}: SchemaFieldPickerProps) {
  const { entitySchema, isLoading, error } = useCommendaOSEntitySchema(entity);

  // Build the dropdown menu from schema
  const menu = useMemo(() => {
    if (!entitySchema?.fields) {
      return { groups: [] };
    }

    const items = buildFieldMenuItems(
      entitySchema.fields,
      '',
      [],
      (path, label, breadcrumb, fieldTypeInfo) => {
        onSelect(path, label, fieldTypeInfo);
      },
      readableOnly,
      editableOnly
    );

    return {
      groups: [{ items }],
    };
  }, [entitySchema, onSelect, readableOnly, editableOnly]);

  // Find the currently selected field info
  const selectedFieldInfo = useMemo(() => {
    if (!value || !entitySchema?.fields) return null;
    return findFieldByPath(entitySchema.fields, value);
  }, [value, entitySchema]);

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('', '');
  };

  // Loading state
  if (isLoading) {
    return (
      <Button
        variant="secondary"
        className="w-full justify-between text-left font-normal"
        disabled
      >
        <span className="flex items-center gap-2 text-text-tertiary">
          <LoadingIndicator size={16} />
          Loading data...
        </span>
      </Button>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-2">
        <Button
          variant="secondary"
          className="w-full justify-between text-left font-normal border-border-error"
          disabled
        >
          <span className="text-text-error">Failed to load schema</span>
        </Button>
        <p className="txt-xs-regular text-text-tertiary">
          Enter path manually if needed
        </p>
      </div>
    );
  }

  // Empty state - no fields available
  if (menu.groups.length === 0 || menu.groups[0].items.length === 0) {
    return (
      <Button
        variant="secondary"
        className="w-full justify-between text-left font-normal"
        disabled
      >
        <span className="text-text-tertiary">No fields available</span>
      </Button>
    );
  }

  // Render the trigger button content
  const renderTriggerContent = () => {
    if (selectedFieldInfo) {
      return (
        <div className="flex items-center justify-between w-full min-w-0">
          <div className="flex flex-col items-start min-w-0 flex-1">
            <span className="text-text-primary truncate w-full">
              {selectedFieldInfo.breadcrumb.join(' > ')}
            </span>
            <span className="txt-xs-regular text-text-tertiary truncate w-full">
              {selectedFieldInfo.path}
            </span>
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-bg-secondary rounded"
            >
              <X className="w-3 h-3 text-text-tertiary" />
            </button>
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between w-full">
        <span className="text-text-tertiary">{placeholder}</span>
        <ChevronDown className="w-4 h-4 text-text-tertiary" />
      </div>
    );
  };

  return (
    <Dropdown
      menu={menu}
      align="start"
      side="bottom"
      sideOffset={4}
      contentClassName="max-h-[300px] overflow-y-auto"
    >
      <Button
        variant="secondary"
        className="w-full justify-between text-left font-normal h-auto min-h-[40px] py-2"
      >
        {renderTriggerContent()}
      </Button>
    </Dropdown>
  );
}
